import { Channel, ChannelPair, Montage } from "../../Montage/types/montage.types";

export type InterchangeFormat = "json" | "csv" | "tsv" | "xml" | "locs";

const MONTAGE_TYPES = new Set<Montage["montage_type"]>([
  "bipolar",
  "referential",
  "laplacian",
  "average_reference",
  "passthrough",
]);

const csvEscape = (value: unknown) => {
  const text = value === undefined || value === null ? "" : String(value);
  return /[",\n\r\t]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const xmlEscape = (value: unknown) =>
  (value === undefined || value === null ? "" : String(value))
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const splitDelimitedRows = (content: string, delimiter: "," | "\t") => {
  const rows: string[][] = [];
  let field = "";
  let row: string[] = [];
  let quoted = false;

  for (let index = 0; index < content.length; index += 1) {
    const char = content[index];
    const next = content[index + 1];

    if (char === '"') {
      if (quoted && next === '"') {
        field += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
      continue;
    }

    if (!quoted && char === delimiter) {
      row.push(field.trim());
      field = "";
      continue;
    }

    if (!quoted && (char === "\n" || char === "\r")) {
      if (char === "\r" && next === "\n") index += 1;
      row.push(field.trim());
      if (row.some((cell) => cell.length > 0)) rows.push(row);
      field = "";
      row = [];
      continue;
    }

    field += char;
  }

  row.push(field.trim());
  if (row.some((cell) => cell.length > 0)) rows.push(row);
  return rows;
};

const rowsToObjects = (content: string, delimiter: "," | "\t") => {
  const rows = splitDelimitedRows(content, delimiter);
  const headers = rows[0]?.map((header) => header.trim().toLowerCase()) ?? [];
  return rows.slice(1).map((row) =>
    Object.fromEntries(headers.map((header, index) => [header, row[index] ?? ""])),
  );
};

const normalizeMontageType = (value: unknown): Montage["montage_type"] => {
  const candidate = String(value || "bipolar").trim().toLowerCase();
  return MONTAGE_TYPES.has(candidate as Montage["montage_type"])
    ? (candidate as Montage["montage_type"])
    : "bipolar";
};

const asNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const detectInterchangeFormat = (fileName: string): InterchangeFormat => {
  const lowerName = fileName.toLowerCase();
  if (lowerName.endsWith(".xml")) return "xml";
  if (
    lowerName.endsWith(".sfp") ||
    lowerName.endsWith(".ced") ||
    lowerName.endsWith(".elp") ||
    lowerName.endsWith(".locs") ||
    lowerName.endsWith(".loc")
  ) {
    return "locs";
  }
  if (lowerName.endsWith(".tsv") || lowerName.endsWith(".bids.tsv")) return "tsv";
  if (lowerName.endsWith(".csv")) return "csv";
  return "json";
};

export const downloadInterchangeFile = (
  filename: string,
  content: string,
  mimeType: string,
) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  // Append before click so browsers treat this as a download, not a popup.
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

export const validateChannelsInterchange = (
  channels: Channel[],
): { valid: boolean; errors: string[]; warnings: string[] } => {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!Array.isArray(channels) || channels.length === 0) {
    errors.push("Import file contains no electrode/channel rows.");
    return { valid: false, errors, warnings };
  }

  const seen = new Set<string>();
  channels.forEach((channel, index) => {
    const code = String(channel?.channel_code || "").trim();
    if (!code) {
      errors.push(`Row ${index + 1} is missing a channel/electrode code.`);
      return;
    }
    const key = code.toUpperCase();
    if (seen.has(key)) {
      warnings.push(`Duplicate electrode code "${code}" in import file.`);
    }
    seen.add(key);

    if (
      channel.position_x !== undefined &&
      channel.position_x !== null &&
      !Number.isFinite(Number(channel.position_x))
    ) {
      errors.push(`Row ${index + 1} (${code}) has an invalid X coordinate.`);
    }
    if (
      channel.position_y !== undefined &&
      channel.position_y !== null &&
      !Number.isFinite(Number(channel.position_y))
    ) {
      errors.push(`Row ${index + 1} (${code}) has an invalid Y coordinate.`);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
};

export const serializeMontage = (
  montage: Montage,
  format: InterchangeFormat = "json",
) => {
  if (format === "json") {
    return JSON.stringify(
      {
        schema: "nervedx.eeg.montage",
        version: "1.0.0",
        exported_at: new Date().toISOString(),
        montage,
      },
      null,
      2,
    );
  }

  if (format === "xml") {
    const pairs = (montage.channel_pairs || [])
      .map(
        (pair, index) =>
          `    <channelPair index="${index + 1}" positive="${xmlEscape(pair.positive)}" negative="${xmlEscape(pair.negative)}" label="${xmlEscape(pair.label)}" region="${xmlEscape(pair.region)}" visible="${pair.visible !== false}" />`,
      )
      .join("\n");
    return [
      '<?xml version="1.0" encoding="UTF-8"?>',
      `<montage name="${xmlEscape(montage.montage_name)}" type="${xmlEscape(montage.montage_type)}">`,
      `  <description>${xmlEscape(montage.description)}</description>`,
      "  <channelPairs>",
      pairs,
      "  </channelPairs>",
      "</montage>",
      "",
    ].join("\n");
  }

  const delimiter = format === "tsv" ? "\t" : ",";
  const header = ["montage_name", "montage_type", "description", "positive", "negative", "label", "region", "visible"];
  const rows = (montage.channel_pairs || []).map((pair) =>
    [
      montage.montage_name,
      montage.montage_type,
      montage.description || "",
      pair.positive,
      pair.negative,
      pair.label || "",
      pair.region || "",
      pair.visible === false ? "false" : "true",
    ]
      .map(csvEscape)
      .join(delimiter),
  );
  return [header.join(delimiter), ...rows, ""].join("\n");
};

export const parseMontageInterchange = (
  content: string,
  fileName: string,
): Montage => {
  const format = detectInterchangeFormat(fileName);

  if (format === "json") {
    const parsed = JSON.parse(content);
    const source = parsed.montage ?? parsed;
    return {
      montage_name: String(source.montage_name ?? source.name ?? "Imported Montage"),
      montage_type: normalizeMontageType(source.montage_type ?? source.type),
      description: source.description,
      is_default: false,
      is_shared: false,
      channel_pairs: Array.isArray(source.channel_pairs)
        ? source.channel_pairs.map((pair: any) => ({
            positive: String(pair.positive ?? pair.positive_code ?? pair.source ?? ""),
            negative: String(pair.negative ?? pair.negative_code ?? pair.target ?? ""),
            label: pair.label,
            region: pair.region,
            visible: pair.visible === undefined ? true : Boolean(pair.visible),
          })).filter((pair: ChannelPair) => pair.positive)
        : [],
    };
  }

  if (format === "xml") {
    const document = new DOMParser().parseFromString(content, "application/xml");
    const parseError = document.querySelector("parsererror");
    if (parseError) throw new Error("Invalid XML file");
    const root = document.querySelector("montage");
    if (!root) throw new Error("Missing montage root element");
    const pairs = Array.from(document.querySelectorAll("channelPair, pair")).map((node) => ({
      positive: node.getAttribute("positive") || node.getAttribute("source") || "",
      negative: node.getAttribute("negative") || node.getAttribute("target") || "",
      label: node.getAttribute("label") || undefined,
      region: node.getAttribute("region") || undefined,
      visible: node.getAttribute("visible") !== "false",
    }));
    return {
      montage_name: root.getAttribute("name") || "Imported Montage",
      montage_type: normalizeMontageType(root.getAttribute("type")),
      description: document.querySelector("description")?.textContent || undefined,
      is_default: false,
      is_shared: false,
      channel_pairs: pairs.filter((pair) => pair.positive),
    };
  }

  if (format === "locs") {
    const rows = content
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line && !line.startsWith("#") && !line.startsWith("%"))
      .map((line) => line.split(/[\s,;]+/));
    return {
      montage_name: "Imported Montage",
      montage_type: "bipolar",
      is_default: false,
      is_shared: false,
      channel_pairs: rows
        .map((row) => ({
          positive: row[0] || "",
          negative: row[1] || "",
          label: row[2],
          visible: true,
        }))
        .filter((pair) => pair.positive),
    };
  }

  const rows = rowsToObjects(content, format === "tsv" ? "\t" : ",");
  const first = rows[0] ?? {};
  return {
    montage_name: first.montage_name || first.name || "Imported Montage",
    montage_type: normalizeMontageType(first.montage_type || first.type),
    description: first.description,
    is_default: false,
    is_shared: false,
    channel_pairs: rows
      .map((row) => ({
        positive: row.positive || row.positive_code || row.source || row.active || row.electrode || "",
        negative: row.negative || row.negative_code || row.target || row.reference || "",
        label: row.label || undefined,
        region: row.region || undefined,
        visible: row.visible !== "false",
      }))
      .filter((pair) => pair.positive),
  };
};

export interface InterchangeValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export const validateMontageInterchange = (
  montage: Partial<Montage> | null | undefined,
): InterchangeValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!montage || typeof montage !== "object") {
    return {
      valid: false,
      errors: ["File does not contain a montage object."],
      warnings,
    };
  }

  if (!String(montage.montage_name || "").trim()) {
    errors.push("Missing montage_name.");
  }

  if (!MONTAGE_TYPES.has(montage.montage_type as Montage["montage_type"])) {
    errors.push(
      `Unsupported montage_type "${String(montage.montage_type || "")}".`,
    );
  }

  if (!Array.isArray(montage.channel_pairs)) {
    errors.push("Missing channel_pairs array.");
  } else if (montage.channel_pairs.length === 0) {
    errors.push("Montage must contain at least one channel pair.");
  } else {
    montage.channel_pairs.forEach((pair, index) => {
      if (!String(pair?.positive || "").trim()) {
        errors.push(`Channel pair ${index + 1} is missing positive electrode.`);
      }
      if (!String(pair?.negative || "").trim()) {
        warnings.push(`Channel pair ${index + 1} has no negative/reference electrode.`);
      }
    });
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
};

export const serializeChannels = (
  channels: Channel[],
  format: InterchangeFormat = "json",
) => {
  if (format === "json") {
    return JSON.stringify(
      {
        schema: "nervedx.eeg.electrode-layout",
        bids_compatible: true,
        version: "1.0.0",
        exported_at: new Date().toISOString(),
        channels,
      },
      null,
      2,
    );
  }

  if (format === "xml") {
    const rows = channels
      .map(
        (channel) =>
          `  <electrode name="${xmlEscape(channel.channel_code)}" x="${xmlEscape(channel.position_x)}" y="${xmlEscape(channel.position_y)}" z="${xmlEscape(channel.position_z)}" system="${xmlEscape(channel.electrode_system)}" region="${xmlEscape(channel.region)}" />`,
      )
      .join("\n");
    return [
      '<?xml version="1.0" encoding="UTF-8"?>',
      '<electrodeLayout schema="nervedx.eeg.electrode-layout">',
      rows,
      "</electrodeLayout>",
      "",
    ].join("\n");
  }

  const delimiter = format === "tsv" ? "\t" : ",";
  const header =
    format === "tsv"
      ? ["name", "x", "y", "z", "type", "system", "region", "hemisphere"]
      : ["channel_code", "channel_name", "electrode_system", "region", "hemisphere", "position_x", "position_y", "position_z", "display_order", "default_color", "is_active"];
  const rows = channels.map((channel) => {
    const values =
      format === "tsv"
        ? [
            channel.channel_code,
            channel.digitized_x ?? "n/a",
            channel.digitized_y ?? "n/a",
            channel.digitized_z ?? "n/a",
            "EEG",
            channel.electrode_system,
            channel.region,
            channel.hemisphere,
          ]
        : [
            channel.channel_code,
            channel.channel_name,
            channel.electrode_system,
            channel.region,
            channel.hemisphere,
            channel.position_x,
            channel.position_y,
            channel.position_z,
            channel.display_order,
            channel.default_color,
            channel.is_active,
          ];
    return values.map(csvEscape).join(delimiter);
  });
  return [header.join(delimiter), ...rows, ""].join("\n");
};

export const parseChannelsInterchange = (
  content: string,
  fileName: string,
): Channel[] => {
  const format = detectInterchangeFormat(fileName);

  if (format === "json") {
    const parsed = JSON.parse(content);
    const rows = parsed.channels ?? parsed.electrodes ?? parsed;
    if (!Array.isArray(rows)) throw new Error("JSON file does not contain channels");
    return rows.map((row: any, index: number) => channelFromRecord(row, index));
  }

  if (format === "xml") {
    const document = new DOMParser().parseFromString(content, "application/xml");
    if (document.querySelector("parsererror")) throw new Error("Invalid XML file");
    return Array.from(document.querySelectorAll("electrode, channel")).map((node, index) =>
      channelFromRecord(
        {
          channel_code: node.getAttribute("name") || node.getAttribute("code"),
          channel_name: node.getAttribute("label"),
          electrode_system: node.getAttribute("system"),
          region: node.getAttribute("region"),
          hemisphere: node.getAttribute("hemisphere"),
          position_x: node.getAttribute("x"),
          position_y: node.getAttribute("y"),
          position_z: node.getAttribute("z"),
        },
        index,
      ),
    );
  }

  if (format === "locs") {
    return content
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line && !line.startsWith("#") && !line.startsWith("%"))
      .map((line) => line.split(/[\s,;]+/))
      .map((parts, index) => {
        const firstIsIndex = /^\d+$/.test(parts[0] || "");
        const code = firstIsIndex ? parts[1] : parts[0];
        const xOffset = firstIsIndex ? 2 : 1;
        const channel = channelFromRecord(
          {
            channel_code: code,
            position_x: parts[xOffset],
            position_y: parts[xOffset + 1],
            position_z: parts[xOffset + 2],
            electrode_system: "Imported",
          },
          index,
        );
        return { ...channel, digitized_x: channel.position_x, digitized_y: channel.position_y, digitized_z: channel.position_z, position_x: undefined, position_y: undefined, position_z: undefined };
      });
  }

  return rowsToObjects(content, format === "tsv" ? "\t" : ",").map((row, index) => {
    const channel = channelFromRecord(row, index);
    return format === "tsv"
      ? { ...channel, digitized_x: channel.position_x, digitized_y: channel.position_y, digitized_z: channel.position_z, position_x: undefined, position_y: undefined, position_z: undefined }
      : channel;
  });
};

const channelFromRecord = (row: Record<string, any>, index: number): Channel => {
  const code = String(row.channel_code ?? row.name ?? row.label ?? row.electrode ?? "").trim();
  return {
    channel_id: `import-${Date.now()}-${index}`,
    channel_code: code || `E${index + 1}`,
    channel_name: String(
      row.channel_name ?? row.description ?? (code || `Electrode ${index + 1}`),
    ),
    electrode_system: String(row.electrode_system ?? row.system ?? row.cap ?? "Imported"),
    hemisphere: String(row.hemisphere ?? "midline"),
    region: String(row.region ?? row.type ?? "eeg"),
    position_x: asNumber(row.position_x ?? row.x),
    position_y: asNumber(row.position_y ?? row.y),
    position_z: asNumber(row.position_z ?? row.z),
    display_order: asNumber(row.display_order ?? row.order) ?? index + 1,
    default_color: row.default_color || "#0d6efd",
    abbreviation: row.abbreviation,
    is_standard: row.is_standard === true || row.is_standard === "true",
    age_group: row.age_group || "all",
    is_active: row.is_active === undefined ? true : row.is_active !== false && row.is_active !== "false",
  };
};
